import { z } from 'zod';
export const AttendeeStatusSchema = z.enum(['invited', 'active', 'revoked']);
export const AttendeePublicDtoSchema = z.object({
    id: z.string().uuid(),
    eventId: z.string().uuid(),
    displayName: z.string(),
    company: z.string().nullable(),
    title: z.string().nullable(),
    bio: z.string().nullable(),
    socialLinks: z.record(z.string()),
    interests: z.array(z.string()),
    avatarPath: z.string().nullable().default(null),
    visibleInList: z.boolean(),
    /**
     * The short code printed on the badge card, so staff can type it when a
     * scanner fails. Six characters, stored without the display dash.
     *
     * On the PUBLIC shape rather than the admin one, unlike `qrToken`, and the
     * distinction is the whole point: a badge code is printed on a lanyard and
     * read by everyone standing near it, and holding one grants nothing — every
     * endpoint that accepts one already requires a moderator or an org admin. The
     * QR token is the opposite: it admits its holder and adds them as a connection
     * on its own, which is why it never leaves the process except inside the
     * pixels of a code.
     *
     * Defaulted so a client built against an older API — the app in someone's
     * pocket — keeps parsing a response that predates the column.
     */
    badgeCode: z.string().default(''),
});
export const AttendeeSelfDtoSchema = AttendeePublicDtoSchema.extend({
    invitedEmail: z.string().email(),
    qrToken: z.string(),
});
/**
 * What a door sends to name someone: the badge it scanned, or the code someone
 * typed off the card when the scanner would not read.
 *
 * The console's and the app's copy of `BadgeCheckInSchema` in
 * `backend/src/schemas/attendees.ts`, and pinned to it — including the
 * exactly-one rule — in `backend/tests/contracts.test.ts`. A client that let a
 * user submit both would be sending two identifiers that may name two different
 * people and letting the server choose.
 *
 * `badgeCode` is loose on purpose. The alphabet and the check character are the
 * server's to enforce so that all three doors answer identically; a client that
 * wants to validate before spending a round trip mirrors the formula documented
 * in `badge_code_bp.ts` rather than tightening this.
 */
export const BadgeCheckInRequestSchema = z
    .object({
    qrToken: z.string().min(8).max(128).optional(),
    badgeCode: z.string().min(1).max(32).optional(),
})
    .refine((b) => (b.qrToken === undefined) !== (b.badgeCode === undefined), {
    message: 'send exactly one of qrToken or badgeCode',
});
export const BulkInviteRequestSchema = z.object({
    rows: z
        .array(z.object({
        email: z.string().email(),
        displayName: z.string().min(1).max(200).optional(),
    }))
        .min(1)
        .max(5000),
});
export const PatchProfileRequestSchema = z.object({
    displayName: z.string().min(1).max(200).optional(),
    company: z.string().max(200).nullable().optional(),
    title: z.string().max(200).nullable().optional(),
    bio: z.string().max(2000).nullable().optional(),
    socialLinks: z.record(z.string().url()).optional(),
    interests: z.array(z.string().min(1).max(40)).max(20).optional(),
    visibleInList: z.boolean().optional(),
    avatarPath: z.string().max(1024).nullable().optional(),
});
//# sourceMappingURL=attendees.js.map